import { clearResearchBrowserState, RESEARCH_READER_STORAGE } from "@heyday/reader-auth/contract";

/**
 * What the reader-session endpoint said. Only an answer from the site is authoritative:
 * `verified` names the reader, `signed-out` means there is no session (or it was revoked).
 * A timeout, a dropped connection, a 5xx or an answer that is not the session's shape is
 * `unreachable`: the session may well be valid, so it never clears the reader's state.
 */
export type ReaderSessionAnswer =
  | { readonly kind: "verified"; readonly readerId: string; readonly email: string }
  | { readonly kind: "signed-out" }
  | { readonly kind: "unreachable" };

const UNREACHABLE: ReaderSessionAnswer = { kind: "unreachable" };
const SIGNED_OUT: ReaderSessionAnswer = { kind: "signed-out" };

/** Classifies one response from `/api/reader/session`. */
export function classifyReaderSession(status: number, body: unknown): ReaderSessionAnswer {
  if (status >= 500 || status === 408 || status === 429) return UNREACHABLE;
  if (status === 401 || status === 403) return SIGNED_OUT;
  if (
    status < 200 ||
    status >= 300 ||
    typeof body !== "object" ||
    body === null ||
    !("state" in body)
  )
    return UNREACHABLE;
  if (body.state !== "verified") return SIGNED_OUT;
  if (
    !("reader" in body) ||
    typeof body.reader !== "object" ||
    body.reader === null ||
    !("id" in body.reader) ||
    typeof body.reader.id !== "string" ||
    !("email" in body.reader) ||
    typeof body.reader.email !== "string"
  )
    return UNREACHABLE;
  return { kind: "verified", readerId: body.reader.id, email: body.reader.email };
}

async function askReaderSession(): Promise<ReaderSessionAnswer> {
  try {
    const response = await fetch("/api/reader/session", {
      credentials: "same-origin",
      cache: "no-store",
      signal: AbortSignal.timeout(10_000),
    });
    const body: unknown = await response.json().catch(() => null);
    return classifyReaderSession(response.status, body);
  } catch {
    return UNREACHABLE;
  }
}

/**
 * What a check of an open session does. A different reader (or none) leaves; an unreachable
 * site leaves everything as it is until a later check gets an answer.
 */
export function readerSessionChange(
  current: string,
  answer: ReaderSessionAnswer,
): "stay" | "signed-out" | "other-reader" {
  if (answer.kind === "unreachable") return "stay";
  if (answer.kind === "signed-out") return "signed-out";
  return answer.readerId === current ? "stay" : "other-reader";
}

const INITIAL_ATTEMPTS = 3;

/** The heyday.news reader this page belongs to. */
export interface VerifiedReader {
  readonly id: string;
  readonly email: string;
}

/**
 * Runs before importing any application stores. Resolves with the reader, or `null` when
 * the page is leaving for the sign-in.
 */
export async function initializeReaderSession(): Promise<VerifiedReader | null> {
  const leave = (signedIn: boolean) => {
    clearResearchBrowserState(localStorage);
    // On heyday.news HeyDex runs in the site's isolated frame, which cannot hold the site's
    // own pages: the sign-in replaces the whole page. In the app's web view top is HeyDex.
    (window.top ?? window).location.replace(signedIn ? "/heydex/" : "/signin?next=%2Fheydex%2F");
  };
  // Nothing private renders until the site has answered; an unreachable site is a
  // startup failure with a reload, never a sign-out.
  let answer = await askReaderSession();
  for (let attempt = 1; answer.kind === "unreachable" && attempt < INITIAL_ATTEMPTS; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, attempt * 1_000));
    answer = await askReaderSession();
  }
  if (answer.kind === "unreachable") throw new Error("The reader session could not be checked.");
  if (answer.kind === "signed-out") {
    leave(false);
    return null;
  }
  const current = answer.readerId;
  if (localStorage.getItem(RESEARCH_READER_STORAGE) !== current) {
    clearResearchBrowserState(localStorage);
    localStorage.setItem(RESEARCH_READER_STORAGE, current);
  }
  let checking = false;
  // A hidden tab asks nothing, whatever wakes it: it checks again the moment it is shown.
  const check = async () => {
    if (checking || document.visibilityState !== "visible") return;
    checking = true;
    try {
      const change = readerSessionChange(current, await askReaderSession());
      if (change !== "stay") leave(change === "other-reader");
    } finally {
      checking = false;
    }
  };
  const focus = () => {
    void check();
  };
  const timer = setInterval(focus, 15000);
  document.addEventListener("visibilitychange", focus);
  const channel =
    typeof BroadcastChannel === "undefined" ? undefined : new BroadcastChannel("heyday:reader");
  channel?.addEventListener("message", focus);
  window.addEventListener("focus", focus);
  window.addEventListener("storage", (event) => {
    if (event.key === RESEARCH_READER_STORAGE) focus();
  });
  window.addEventListener("pagehide", (event) => {
    if (!event.persisted) {
      clearInterval(timer);
      channel?.close();
    }
  });
  return { id: current, email: answer.email };
}
